import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, X, User } from "@phosphor-icons/react";

/**
 * Edit a list of authors — each row is { name, email, department }.
 * Supports multiple authors (e.g. three seniors on one article).
 * `staff` (optional) powers an email autocomplete datalist.
 */
export default function AuthorsEditor({ value, onChange, staff = [], readOnly = false, testid = "authors" }) {
  const authors = value && value.length ? value : [{ name: "", email: "", department: "" }];

  const patch = (i, p) => onChange(authors.map((a, idx) => (idx === i ? { ...a, ...p } : a)));
  const add = () => onChange([...authors, { name: "", email: "", department: "" }]);
  const remove = (i) => {
    const next = authors.filter((_, idx) => idx !== i);
    onChange(next.length ? next : [{ name: "", email: "", department: "" }]);
  };

  // When an email matches a known staff member, auto-fill name + department.
  const onEmailBlur = (i, email) => {
    const match = staff.find((u) => u.email?.toLowerCase() === (email || "").toLowerCase());
    if (match) patch(i, { name: authors[i].name || match.name || "", department: match.department || "" });
  };

  if (readOnly) {
    return (
      <div className="space-y-1" data-testid={`${testid}-readonly`}>
        {(value || []).length === 0 ? (
          <span className="text-sm text-slate-400">—</span>
        ) : (
          value.map((a, i) => (
            <div key={i} className="text-sm text-slate-700 flex items-center gap-2">
              <User size={13} className="text-slate-400" />
              <span className="font-medium">{a.name || a.email}</span>
              {a.email && <span className="text-slate-400 font-mono text-xs">{a.email}</span>}
              {a.department && <span className="text-xs text-slate-500">· {a.department}</span>}
            </div>
          ))
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={testid}>
      <datalist id={`${testid}-staff-emails`}>
        {staff.filter((u) => u.role !== "guest").map((u) => (
          <option key={u.id || u.email} value={u.email}>{u.name}</option>
        ))}
      </datalist>
      {authors.map((a, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`${testid}-row-${i}`}>
          <Input
            data-testid={`${testid}-name-${i}`}
            value={a.name || ""}
            onChange={(e) => patch(i, { name: e.target.value })}
            placeholder="Author name"
            className="rounded-sm h-9 text-sm"
          />
          <Input
            data-testid={`${testid}-email-${i}`}
            list={`${testid}-staff-emails`}
            value={a.email || ""}
            onChange={(e) => patch(i, { email: e.target.value })}
            onBlur={(e) => onEmailBlur(i, e.target.value)}
            placeholder="email@barnardinc.co.za"
            className="rounded-sm h-9 text-sm"
          />
          <button
            type="button"
            data-testid={`${testid}-remove-${i}`}
            onClick={() => remove(i)}
            disabled={authors.length === 1 && !a.name && !a.email}
            className="text-slate-400 hover:text-rose-600 disabled:opacity-30 p-1"
            title="Remove author"
          >
            <X size={14} />
          </button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        data-testid={`${testid}-add`}
        className="rounded-sm gap-1 h-8 text-xs border-slate-300"
      >
        <Plus size={12} /> Add author
      </Button>
    </div>
  );
}
